/**
 * LMS sidebar navigation items (issue #7).
 *
 * Adapted from standalone-search's sidebar-items.ts.
 * Three distinct nav sets — admin, instructor, student — returned by
 * `sidebarItemsForRole()`. AppSidebar passes the role-resolved items
 * to NavMain, which renders them.
 *
 * Actual route paths will align with feature pages (#10–#17).
 * `comingSoon` marks sections that will be wired in future milestones.
 */

import {
  BookOpen,
  FolderOpen,
  GraduationCap,
  Landmark,
  Mail,
  Presentation,
  Settings,
  type LucideIcon,
} from "lucide-react";

import type { Role } from "@/contexts/AuthContext";
import { PROTECTED_ROUTES } from "@/navigation/routes";

export type NavMainItem = {
  title: string;
  url: string;
  /** Required: the collapsed rail shows nothing else, so every destination
   * needs a mark of its own. Each seat's home names its seat. */
  icon: LucideIcon;
  comingSoon?: boolean;
  newTab?: boolean;
  /** Keep this collection entry active on a segment-safe child route. */
  activeOnDescendants?: boolean;
  subItems?: NavMainItem[];
};

export type NavGroup = {
  id: string;
  label?: string;
  items: NavMainItem[];
};

export type RegistrarWorkspaceSection = "Overview" | "Admissions" | "People" | "Courses" | "History";

const isExactOrDescendant = (pathname: string, url: string) =>
  pathname === url || pathname.startsWith(`${url}/`);

/**
 * Return the registrar section that owns a route, by path only, without
 * absorbing adjacent admin destinations such as Settings, the Inbox, or My
 * Teaching. A record and every view of it sit under its list's section: a
 * course (`/admin/courses/{id}`) under Courses, an applicant file
 * (`/admin/admissions/{id}`) under Admissions, and a person
 * (`/admin/people/{key}`) or an invitation (`/admin/invitations/{id}`) under
 * People. Retired addresses own no section: they redirect on arrival.
 */
export function registrarWorkspaceSectionForPath(pathname: string): RegistrarWorkspaceSection | null {
  if (pathname === PROTECTED_ROUTES.admin.dashboard) return "Overview";
  if (isExactOrDescendant(pathname, PROTECTED_ROUTES.admin.admissions)) return "Admissions";
  if (
    [PROTECTED_ROUTES.admin.people, PROTECTED_ROUTES.admin.invitations]
      .some((url) => isExactOrDescendant(pathname, url))
  ) return "People";
  if (
    [PROTECTED_ROUTES.admin.courses, PROTECTED_ROUTES.admin.academicStructure]
      .some((url) => isExactOrDescendant(pathname, url))
  ) return "Courses";
  if (isExactOrDescendant(pathname, PROTECTED_ROUTES.admin.courseHistory)) return "History";
  return null;
}

export function isRegistrarWorkspacePath(pathname: string): boolean {
  return registrarWorkspaceSectionForPath(pathname) !== null;
}

const adminItems: NavGroup[] = [
  {
    id: "admin-main",
    label: "Registrar",
    items: [
      {
        title: "Registrar Workspace",
        url: PROTECTED_ROUTES.admin.dashboard,
        icon: Landmark,
      },
    ],
  },
  {
    id: "admin-settings",
    items: [
      {
        title: "Institution Settings",
        url: PROTECTED_ROUTES.admin.settings,
        activeOnDescendants: true,
        icon: Settings,
      },
    ],
  },
  {
    id: "admin-teaching",
    label: "My Teaching",
    items: [
      {
        title: "Home",
        url: PROTECTED_ROUTES.adminTeaching.dashboard,
        icon: Presentation,
      },
      { title: "Inbox", url: PROTECTED_ROUTES.adminTeaching.inbox, icon: Mail },
      { title: "Course folders", url: PROTECTED_ROUTES.adminTeaching.drive, icon: FolderOpen },
    ],
  },
];

const instructorItems: NavGroup[] = [
  {
    id: "instructor-main",
    label: "My Teaching",
    items: [
      {
        title: "Home",
        url: PROTECTED_ROUTES.instructor.dashboard,
        icon: Presentation,
      },
      { title: "Inbox", url: PROTECTED_ROUTES.instructor.inbox, icon: Mail },
      { title: "Course folders", url: PROTECTED_ROUTES.instructor.drive, icon: FolderOpen },
    ],
  },
];

const studentItems: NavGroup[] = [
  {
    id: "student-main",
    label: "My Learning",
    items: [
      {
        title: "Home",
        url: PROTECTED_ROUTES.student.dashboard,
        icon: BookOpen,
      },
      { title: "Inbox", url: PROTECTED_ROUTES.student.inbox, icon: Mail },
      { title: "All grades", url: PROTECTED_ROUTES.student.grades, icon: GraduationCap },
    ],
  },
];

/**
 * Return the nav groups for the given role.
 * Null role (unauthenticated) returns an empty array — should not occur in
 * practice since AppShell only renders inside ProtectedRoute.
 */
export function sidebarItemsForRole(
  role: Role | null,
  workspaces?: { teaching?: { available: boolean }; learning?: { available: boolean } },
): NavGroup[] {
  const learning = workspaces?.learning?.available ? studentItems : [];
  switch (role) {
    case "admin":
      return [
        ...adminItems.filter((group) => group.id !== "admin-teaching" || workspaces?.teaching?.available === true),
        ...learning,
      ];
    case "instructor":
      return [...instructorItems, ...learning];
    case "student":
      return learning;
    default:
      return [];
  }
}

/**
 * The nav group that owns a path, by longest matching item URL.
 *
 * Longest-match rather than first-match because the admin nav nests: both
 * `/admin` (Registrar Dashboard) and `/admin/teaching/inbox` (My
 * Teaching) are prefixes of the latter, and first-match would file every
 * teaching page under Registrar. Matching is on whole path segments, so
 * `/admin/teachingxyz` cannot match `/admin/teaching`.
 *
 * Every role's navigation is searched rather than one selected by the caller's
 * role, because the three sets are disjoint by URL prefix and a path therefore
 * names its own workspace. That keeps the lookup a pure function of the path:
 * asking the caller for a role would make a page's eyebrow depend on auth
 * context that most of these pages otherwise never touch.
 *
 * This exists so a page can state where it sits by asking the navigation
 * instead of repeating it. Ten of the fifteen pre-ADR-0010 admin destinations
 * had drifted from their own group - naming groups that do not exist
 * ("Academics", "Admin"), reversing the pair, or in the case of `/admin/courses`
 * naming a different destination entirely - because each page held its own
 * copy of an answer the nav already had.
 */
export function navGroupForPath(pathname: string): NavGroup | null {
  let best: NavGroup | null = null;
  let bestLength = -1;
  for (const group of [...adminItems, ...instructorItems, ...studentItems]) {
    for (const item of group.items) {
      const matches = pathname === item.url || pathname.startsWith(`${item.url}/`);
      if (matches && item.url.length > bestLength) {
        best = group;
        bestLength = item.url.length;
      }
    }
  }
  return best;
}
